/**
 * ============================================================================
 * ⭐⭐ THE CHIP ROW'S METRICS, IN ONE PLACE
 * ============================================================================
 *
 * Extracted from `StatusFilterBar.tsx` on 2026-09-19 so the subject chips
 * (`SubjectChips`, decision 188) and the status chips are the same size, the same
 * weight and the same shape BY CONSTRUCTION rather than by somebody matching them.
 *
 * ---- ⛔⛔ WHY THIS IS A MODULE AND NOT A COMMENT SAYING "KEEP THESE IN SYNC" --
 *
 * §1AV.170: `StatusFilterBar` was itself extracted out of `Calendar.tsx` and the
 * ORIGINAL WAS NEVER DELETED, so Day and Week/Month drifted for a day and a half
 * — every refinement landing in one of the two copies.
 *
 *   ** AN EXTRACTION THAT DOES NOT DELETE THE ORIGINAL IS A COPY. **
 *   ** TWO ROWS THAT LOOK ALIKE BECAUSE SOMEBODY MATCHED THEM WILL STOP LOOKING
 *      ALIKE AT THE FIRST REFINEMENT OF EITHER. THE ONLY DURABLE "SAME" IS
 *      "SAME CODE". **
 *
 * ⛔ SO `StatusFilterBar` NO LONGER DECLARES THESE TOKENS. It reads them from
 * here, and a guard asserts it does not re-declare them.
 *
 * ---- ⚠️ WHAT IS **NOT** HERE, AND WHY ---------------------------------------
 *
 * ⛔ The swatch, the tick and the selected ring are NOT here. They belong to a
 * multi-select filter: a marker that has to read as ON or OFF.
 * ⚠️ Hoisting them "for symmetry" would hand the subject row a vocabulary it must
 * then decide not to use.
 *
 *   ** SHARE WHAT THE TWO ROWS ARE, NOT WHAT ONE OF THEM DOES. **
 *
 * ⛔⤴ ~~"The subject chips are single-select and have no marker at all — the active
 * one is filled."~~ STRUCK 2026-09-19 (§0.3). Pete looked at the filled chip on his
 * own screen and refused it twice in one message — first for a box that matched the
 * Day/Week/Month toggle, then for no box at all: *"leave the check next to the
 * selected item but remove the box around the name."* The subject row now draws a
 * **tick**, exactly like the status row, and the two rows differ by SEPARATOR
 * (`|` vs whitespace), not by marker.
 *
 * ⚠️ WHICH DOES **NOT** MOVE THE TICK INTO THIS FILE. The status tick lives inside a
 * coloured swatch and must contrast with it (`text-white`, `strokeWidth={3}`); the
 * subject tick sits on the page background and takes the text colour. Same glyph,
 * different job — and a shared `tick` token would have to pick one of them.
 * ============================================================================
 */

export interface ChipMetrics {
    /** Horizontal gap BETWEEN chips. */
    gapX: string;
    /** Gap INSIDE a chip, between its marker and its label. */
    gapIn: string;
    /** A chip's padding. */
    pad: string;
    /** The marker box — status chips only; sized so a tick is readable in it. */
    swatch: string;
    /** The tick glyph — status chips only. */
    tick: string;
    /** The selected ring — status chips only. */
    ring: string;
    /** Marker corner: dots when dense, squares otherwise. Status chips only. */
    round: string;
}

/**
 * ⚠️ `dense` IS THE ONLY INPUT, AND THE VALUES ARE NOT FREE CHOICES.
 * ⛔⤴ `gapX` shipped once as `dense ? 'gap-x-2' : 'gap-x-2'` — a ternary whose
 * branches were equal, i.e. a decision that was never made wearing the clothes of
 * one. `tools/tenancy-row3-guard.cjs` now fails on any `dense ? 'x' : 'x'`.
 * ⛔ And the dense swatch must stay STRICTLY LARGER than a bare dot: it holds a
 * tick, and a glyph inside an 8px circle is a smudge (§1AV.170).
 */
export function chipMetrics(dense: boolean): ChipMetrics {
    return {
        gapX:   dense ? 'gap-x-1.5'     : 'gap-x-2',
        gapIn:  dense ? 'gap-1'         : 'gap-1.5',
        pad:    dense ? 'px-0.5 py-0'   : 'px-1 py-0.5',
        swatch: dense ? 'h-2.5 w-2.5'   : 'h-3.5 w-3.5',
        tick:   dense ? 'h-1.5 w-1.5'   : 'h-2.5 w-2.5',
        ring:   dense ? 'ring-1 ring-foreground/60' : 'ring-2 ring-foreground/50',
        round:  dense ? 'rounded-full'  : 'rounded-sm',
    };
}

/**
 * ============================================================================
 * ⛔⛔⛤ THE FONT SIZE MUST BE ON THE **BUTTON**, AND HERE IS WHY (2026-09-19)
 * ============================================================================
 *
 * `src/index.css` @layer base, lines 117-119:
 *
 *     input, select, button, textarea { font-size: 14px; }
 *
 * ⛔⛔ THAT IS A DIRECT DECLARATION ON THE ELEMENT, SO IT BEATS ANY **INHERITED**
 * SIZE FROM A PARENT — not on specificity, but because inheritance only supplies a
 * value when nothing targets the element at all. Every chip is a `<button>`.
 *
 * ⚠️⚠️ SO FOR FOUR ROUNDS `text-[11px]` AND THEN `text-[10px]` SAT ON THE ROW AND
 * CHANGED NOTHING. Measured live off Pete's screen, mid-argument:
 *
 *     All Status   14px w500      <- button, base rule wins
 *     Requested    14px w400      <- button, base rule wins
 *     Limits       11px w400      <- SPAN, inherits the row, honoured the class
 *     In / Out     12px w400
 *     Trucks       12px w700
 *
 * ⭐⭐⭐ **PETE WAS RIGHT EVERY TIME AND MY MEASUREMENTS WERE OF THE WRONG THING.**
 * He kept saying the chips did not match `Limits`; there really was a 3px gap, and
 * `Limits` really was the only member of the row rendering at the row's own size.
 *
 *   ** READING THE JSX TELLS YOU WHAT THE CLASS SAYS. IT CANNOT TELL YOU WHAT THE
 *      CASCADE DID. A BASE-LAYER ELEMENT RULE IS INVISIBLE FROM THE COMPONENT AND
 *      OUTRANKS EVERY INHERITED VALUE. **
 *   ** WHEN A STYLE CHANGE PRODUCES NO VISIBLE EFFECT, STOP EDITING IT AND GO READ
 *      THE COMPUTED VALUE. FOUR ROUNDS OF "SMALLER" WERE FOUR EDITS TO A PROPERTY
 *      THAT WAS NEVER IN FORCE. **
 *
 * ⛔ THE GLOBAL RULE IS NOT TOUCHED. It governs every input, select and textarea in
 * the app; re-pointing it to fix two rows would be a change of unknown blast radius
 * made for a cosmetic reason.
 *
 * ---- ⭐⭐ SO THE SIZE IS DECLARED TWICE, ON PURPOSE -------------------------
 *
 * On the ROW, for children that inherit (`Limits`, the `|` separators), and on the
 * BUTTON, to beat the base rule. ⛔ They must agree, so neither is a literal at the
 * call site — both read `CHIP_TEXT[size]`, and a guard asserts the pair.
 */
export type ChipSize = 'status' | 'subject';

/**
 * ⛔ TWO ROWS, TWO REFERENCE ELEMENTS — Pete named a different one for each, and I
 * spent three rounds collapsing them into a single size:
 *   - the status row is measured against `Limits`, which sits IN it (11px)
 *   - the subject row started at `In` / `Out` / `Trucks` (12px = text-xs)
 *
 *   ** TWO INSTRUCTIONS THAT EACH NAME A DIFFERENT YARDSTICK ARE TWO INSTRUCTIONS,
 *      NOT ONE INSTRUCTION STATED TWICE. **
 *
 * ⛔⤴ ~~`subject: 'text-xs'`~~ — 12px lasted one deploy. At 340px `Consignee` still
 * wrapped, and Pete worked the problem out loud: *"remove space between the |?"* →
 * ***"Actually, that won't work because of the checkmark"*** → ***"Can you make
 * those 10.5 px and see if that works"***.
 *
 * ⭐⭐ HE WAS RIGHT TO DROP THE GAP IDEA, AND THE REASON IS WORTH KEEPING. The
 * separator gaps are `gap-x-1.5` — a FIXED 6px, not font-relative — and the tick is
 * a fixed 10px box. Squeezing the gaps buys ~18px once; the words are ~150px and
 * they are what actually has to shrink.
 *   ** WHEN A ROW OVERFLOWS, SHRINK WHAT SCALES WITH THE FONT, NOT THE FIXED
 *      FURNITURE BETWEEN IT. THE FURNITURE IS A CONSTANT AND YOU ONLY GET TO SPEND
 *      IT ONCE. **
 *
 * ⚠️ 10.5px IS BELOW ANY TAILWIND SCALE STEP, hence the arbitrary value. It is a
 * literal in this file, so the JIT emits it; built from a variable it would not.
 */
const CHIP_TEXT: Readonly<Record<ChipSize, string>> = {
    status:  'text-[11px]',
    subject: 'text-[10.5px]',
};

/** The flex row both chip rows sit in.
 *
 *  ⚠️ `flex-wrap` is load-bearing: a narrow panel must wrap the chips rather than
 *  clip them or scroll sideways. ⭐ Pete: *"Let it wrap."*
 *  ** A ROW THAT WRAPS COSTS A LINE ONLY WHEN IT MUST; A ROW THAT CLIPS LOSES A
 *     CONTROL AND SAYS NOTHING. **
 *
 *  ⛔ `size` IS REQUIRED AND FIRST, AND THAT IS DELIBERATE. Adding it as an optional
 *  trailing argument would have let every existing call site keep compiling while
 *  silently keeping the wrong size — which is exactly the failure this whole episode
 *  was. ** A CHANGE THAT MUST REACH EVERY CALLER SHOULD BREAK EVERY CALLER. **
 *  ⚠️ `ChipSize` is a string union and `dense` a boolean, so transposing them is a
 *  compile error rather than a silent swap.
 */
export function chipRowClass(size: ChipSize, dense: boolean, className?: string): string {
    return [
        'flex flex-wrap items-center gap-y-1 text-muted-foreground',
        CHIP_TEXT[size],
        chipMetrics(dense).gapX,
        className ?? '',
    ].join(' ');
}

export function chipButtonClass(size: ChipSize, dense: boolean, extra?: string): string {
    const m = chipMetrics(dense);
    return [
        'inline-flex items-center whitespace-nowrap rounded transition-colors',
        /* ⛔⛔ THIS IS THE ONE THAT ACTUALLY TAKES EFFECT. A utility class on the
           element outranks `button { font-size: 14px }` in index.css; the identical
           class on the parent row does not, because that one is only inherited. */
        CHIP_TEXT[size],
        m.gapIn, m.pad, extra ?? '',
    ].join(' ');
}
